/**
 * ENTERPRISE LIMS NAVIGATION ARCHITECTURE
 * 
 * Clean, Workflow-Oriented Architecture:
 * 1. Dashboard (/dashboard)
 * 2. Registration & Billing (Patient Registration, Receipt Settlement, Referral Shares)
 * 3. Sample Management (Sample Collection, Sample Receiving, Outsource Labs)
 * 4. Result Entry & Diagnostics (Pathology Results, Radiology Reports, Microbiology Workbench, Report Approval, Antibiogram)
 * 5. LIMS Masters (Hub + Test, Pricing, Clinical, Organization, Microbiology Masters)
 * 6. Security & Administration (Role & Permissions [RBAC], Employee Master, Audit Trail, System Config)
 */

export const NAVIGATION_MODULES = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    type: 'single',
    path: '/dashboard',
    icon: 'dashboard',
    screenCode: 'DASHBOARD',
    keywords: ['dashboard', 'home', 'overview', 'stats', 'analytics', 'kpi']
  },
  {
    id: 'registration-billing',
    title: 'Registration & Billing',
    type: 'group',
    icon: 'registration',
    items: [
      {
        id: 'appointments',
        title: 'Appointments',
        path: '/appointments',
        icon: 'calendar',
        screenCode: 'APPOINTMENTS',
        keywords: ['appointment', 'booking', 'slot', 'schedule', 'reschedule', 'visit', 'reception', 'walk-in']
      },
      {
        id: 'queue',
        title: 'Queue Desk',
        path: '/queue',
        icon: 'queue',
        screenCode: 'QUEUE',
        keywords: ['queue', 'token', 'counter', 'waiting', 'call next', 'display', 'turn', 'reception']
      },
      {
        id: 'registration',
        title: 'Patient Registration',
        path: '/registration',
        icon: 'registration',
        screenCode: 'REGISTRATION',
        keywords: ['patient', 'registration', 'booking', 'reception', 'demographics', 'admit', 'order']
      },
      {
        id: 'settlement',
        title: 'Receipt Settlement',
        path: '/settlement',
        icon: 'settlement',
        screenCode: 'SETTLEMENT',
        keywords: ['settlement', 'receipt', 'payment', 'cash', 'counter', 'closing', 'refund', 'due', 'billing']
      },
      {
        id: 'discount-approvals',
        title: 'Discount Approvals',
        path: '/discount-approvals',
        icon: 'approval',
        screenCode: 'DISCOUNT_APPROVALS',
        keywords: ['discount', 'approval', 'authority', 'concession', 'pending', 'approver', 'inbox']
      },
      {
        id: 'cancellation-approvals',
        title: 'Cancellation Approvals',
        path: '/cancellation-approvals',
        icon: 'approval',
        screenCode: 'CANCELLATION_APPROVALS',
        keywords: ['cancellation', 'cancel', 'approval', 'authority', 'pending', 'approver', 'inbox']
      },
      {
        id: 'referral-share',
        title: 'Referral Doctor Shares',
        path: '/masters/referral-share',
        icon: 'referral',
        screenCode: 'REFERRAL_SHARE',
        keywords: ['referral', 'doctor', 'share', 'commission', 'payout', 'tariff', 'incentive']
      },
      {
        id: 'carecoins-rules',
        title: 'CareCoins Rules (Wallet)',
        path: '/masters/carecoins-rules',
        icon: 'referral',
        screenCode: 'CARECOINS_RULES',
        keywords: ['carecoins', 'wallet', 'loyalty', 'rewards', 'points', 'redemption', 'earn rate']
      }
    ]
  },
  {
    id: 'sample-management',
    title: 'Sample Management',
    type: 'group',
    icon: 'sample',
    items: [
      {
        id: 'phlebotomy',
        title: 'Sample Collection',
        path: '/phlebotomy',
        icon: 'phlebotomy',
        screenCode: 'PHLEBOTOMY',
        keywords: ['sample', 'collection', 'phlebotomy', 'blood', 'tubes', 'vacutainer', 'barcode', 'draw']
      },
      {
        id: 'sample-receive',
        title: 'Sample Receiving',
        path: '/sample-receive',
        icon: 'sample',
        screenCode: 'SAMPLE_RECEIVE',
        keywords: ['sample', 'receive', 'accessioning', 'inbox', 'barcode', 'rejection', 'accept', 'worklist']
      },
      {
        id: 'outsource-hub',
        title: 'Outsource & Reference Labs',
        path: '/outsource/worklist',
        icon: 'outsource',
        screenCode: 'OUTSOURCE',
        keywords: ['outsource', 'reference', 'lab', 'sendout', 'dispatch', 'manifest', 'partner', 'external']
      }
    ]
  },
  {
    id: 'result-entry',
    title: 'Result Entry & Diagnostics',
    type: 'group',
    icon: 'pathology',
    items: [
      {
        id: 'pathology-worksheet',
        title: 'Smart Pathology Worksheet',
        path: '/pathology/worksheet',
        icon: 'worksheet',
        screenCode: 'WORKSHEET',
        keywords: ['worksheet', 'technician', 'workbench', 'pathology', 'blood', 'hematology', 'biochemistry', 'tat', 'batch', 'print']
      },
      {
        id: 'pathology',
        title: 'Pathology Results',
        path: '/pathology-results',
        icon: 'pathology',
        screenCode: 'PATHOLOGY_RESULTS',
        keywords: ['pathology', 'results', 'biochemistry', 'hematology', 'enter', 'test', 'delta', 'analyzer']
      },
      {
        id: 'microbiology-workbench',
        title: 'Microbiology Workbench (Pathology)',
        path: '/microbiology/worklist',
        icon: 'microbiology',
        screenCode: 'MICROBIOLOGY',
        keywords: ['microbiology', 'pathology', 'culture', 'ast', 'antibiotic', 'organism', 'susceptibility', 'colony', 'isolate']
      },
      {
        id: 'microbiology-antibiogram',
        title: 'Antibiogram & AMR (Pathology)',
        path: '/microbiology/antibiogram',
        icon: 'chart',
        screenCode: 'ANTIBIOGRAM',
        keywords: ['antibiogram', 'pathology', 'amr', 'resistance', 'surveillance', 'epidemiology', 'clsi', 'microbiology']
      },
      {
        id: 'radiology',
        title: 'Radiology Editor / Reports',
        path: '/radiology-results',
        icon: 'radiology',
        screenCode: 'RADIOLOGY_RESULTS',
        keywords: ['radiology', 'editor', 'reporting', 'reports', 'xray', 'ultrasound', 'usg', 'ct', 'mri', 'ecg', 'eeg', 'tmt', 'imaging', 'scan']
      },
      {
        id: 'report-approval',
        title: 'Report Approval & Release',
        path: '/reports/approval-queue',
        icon: 'approval',
        screenCode: 'REPORT_APPROVAL',
        keywords: ['approval', 'queue', 'report', 'doctor', 'sign', 'release', 'verify', 'signature', 'authorize']
      }
    ]
  },
  {
    id: 'masters',
    title: 'LIMS Masters',
    type: 'nested_group',
    icon: 'masters',
    hubPath: '/masters',
    screenCode: 'MASTERS',
    subgroups: [
      {
        id: 'sub-tests',
        title: 'Tests & Investigations',
        icon: 'testtube',
        items: [
          { id: 'm-investigations', title: 'Investigation Master', path: '/masters/investigations', icon: 'testtube', keywords: ['test', 'investigation', 'catalog', 'loinc', 'code'] },
          { id: 'm-profiles', title: 'Profile Master', path: '/masters/profiles', icon: 'profiles', keywords: ['profile', 'panel', 'lft', 'kft', 'lipid', 'group'] },
          { id: 'm-packages', title: 'Package Master', path: '/masters/packages', icon: 'package', keywords: ['package', 'health', 'bundle', 'checkup', 'executive'] },
          { id: 'm-observations', title: 'Observation Master', path: '/masters/observations', icon: 'observations', keywords: ['observation', 'analyte', 'range', 'unit', 'biological'] },
          { id: 'm-formulas', title: 'Formula Intelligence Master', path: '/masters/formulas', icon: 'formula', keywords: ['formula', 'calculation', 'ratio', 'clinical', 'math', 'ai'] },
          { id: 'm-worksheet-templates', title: 'Worksheet Template Master', path: '/masters/worksheet-templates', icon: 'template', keywords: ['worksheet', 'template', 'layout', 'print', 'format', 'pdf', 'technician'] },
          { id: 'm-param-mapping', title: 'Parameter Mapping', path: '/investigation-observation-mapping', icon: 'mapping', keywords: ['mapping', 'parameter', 'analyte', 'formula', 'link'] },
          { id: 'm-report-formats', title: 'Report Layout Master', path: '/masters/report-formats', icon: 'template', keywords: ['report', 'layout', 'format', 'pdf', 'pathology', 'letterhead', 'centre', 'template'] },
          { id: 'm-specimens', title: 'Specimen & Tube Master', path: '/masters/specimens', icon: 'specimens', keywords: ['specimen', 'tube', 'vacutainer', 'matrix', 'color', 'sample'] },
          { id: 'm-barcode-labels', title: 'Barcode Label & Thermal Print Master', path: '/masters/barcode-labels', icon: 'template', keywords: ['barcode', 'label', 'thermal', 'printer', 'zpl', 'tspl', 'tube', 'specimen', '50x25'] },
          { id: 'm-departments', title: 'Department Master', path: '/masters/departments', icon: 'departments', keywords: ['department', 'subspecialty', 'routing', 'lab', 'pathology', 'radiology'] },
          { id: 'm-micro-setup-link', title: 'Microbiology Setup (Pathology)', path: '/masters/microbiology', icon: 'microbiology', keywords: ['microbiology', 'pathology', 'culture', 'ast', 'isolate', 'organism'] }
        ]
      },
      {
        id: 'sub-pricing',
        title: 'Pricing & Contracts',
        icon: 'pricing',
        items: [
          { id: 'm-panels', title: 'Panel Master', path: '/masters/panels', icon: 'panels', keywords: ['panel', 'cash', 'tariff', 'category', 'pup'] },
          { id: 'm-parties', title: 'B2B Client / Party Master', path: '/masters/parties', icon: 'party', keywords: ['b2b', 'party', 'corporate', 'clinic', 'hospital', 'tieup'] },
          { id: 'm-contracts', title: 'Rate Contract Master', path: '/masters/contracts', icon: 'contracts', keywords: ['contract', 'rate', 'price list', 'agreement', 'validity'] },
          { id: 'm-panel-rates', title: 'Panel-Wise Rates', path: '/masters/panel-rates', icon: 'rates', keywords: ['rate', 'panel', 'tariff schedule', 'price list'] },
          { id: 'm-item-rates', title: 'Item-Wise Rates', path: '/masters/item-rates', icon: 'rates', keywords: ['item', 'rate', 'test price', 'tariff comparison'] },
          { id: 'm-centre-panel-mapping', title: 'Center – Panel Mapping', path: '/masters/centre-panel-mapping', icon: 'mapping', keywords: ['mapping', 'center', 'panel', 'authorization', 'centre'] }
        ]
      },
      {
        id: 'sub-clinical',
        title: 'Doctors & Reference Labs',
        icon: 'doctor',
        items: [
          { id: 'm-clinicians', title: 'Referring Clinicians', path: '/masters/clinicians', icon: 'clinicians', keywords: ['clinician', 'doctor', 'consultant', 'referring', 'specialist'] },
          { id: 'm-reference-labs', title: 'Reference Laboratories', path: '/masters/reference-labs', keywords: ['reference', 'external', 'lab', 'partner', 'accreditation'] },
          { id: 'm-outsource-mappings', title: 'Outsource Test Mappings', path: '/masters/outsource-mappings', keywords: ['outsource', 'test code', 'mapping', 'external rate'] },
          { id: 'm-radiology-templates', title: 'Radiology Templates', path: '/masters/radiology-templates', icon: 'template', keywords: ['template', 'radiology', 'docx', 'normal findings'] },
          { id: 'm-doctor-signatures', title: 'Doctor Digital Signatures', path: '/masters/doctor-signatures', icon: 'signature', keywords: ['doctor', 'signature', 'medical', 'authority', 'seal', 'digital'] }
        ]
      },
      {
        id: 'sub-organization',
        title: 'Centers & Locations',
        icon: 'building',
        items: [
          { id: 'm-centres', title: 'Center Master', path: '/masters/centres', icon: 'centre', keywords: ['center', 'centre', 'branch', 'hub', 'nabl', 'facility'] },
          { id: 'm-locations', title: 'Location Hierarchy', path: '/masters/locations', icon: 'location', keywords: ['location', 'city', 'state', 'country', 'pincode', 'address'] }
        ]
      },
      {
        id: 'sub-microbiology',
        title: 'Microbiology Setup (Pathology)',
        icon: 'microbiology',
        items: [
          { id: 'm-micro-hub', title: 'Microbiology Masters Hub', path: '/masters/microbiology', icon: 'microbiology', keywords: ['microbiology', 'pathology', 'hub', 'catalog', 'micro'] },
          { id: 'm-micro-organisms', title: 'Organism Master', path: '/masters/microbiology/organisms', keywords: ['organism', 'bacteria', 'gram', 'taxonomy', 'morphology'] },
          { id: 'm-micro-antimicrobials', title: 'Antimicrobial Master', path: '/masters/microbiology/antimicrobials', keywords: ['antimicrobial', 'antibiotic', 'drug', 'clsi tier'] },
          { id: 'm-micro-panels', title: 'AST Panel Builder', path: '/masters/microbiology/panels', keywords: ['ast', 'panel', 'susceptibility', 'drug panel'] },
          { id: 'm-micro-breakpoints', title: 'Breakpoint Engine', path: '/masters/microbiology/breakpoints', keywords: ['breakpoint', 'eucast', 'clsi', 'sir', 'mic', 'zone'] },
          { id: 'm-micro-rules', title: 'Expert Rules Studio', path: '/masters/microbiology/rules', keywords: ['rules', 'expert', 'mrsa', 'esbl', 'alert', 'intrinsic'] },
          { id: 'm-micro-qc', title: 'Quality Control (QC) Master', path: '/masters/microbiology/qc', keywords: ['qc', 'quality', 'strain', 'control', 'reference strain'] },
          { id: 'm-micro-barcodes', title: 'Barcode & Label Config', path: '/masters/microbiology/barcodes', keywords: ['barcode', 'label', 'printer', 'zpl', 'accession', 'qr', 'datamatrix'] },
          { id: 'm-micro-retention', title: 'Record Retention & Archival', path: '/masters/microbiology/retention', keywords: ['retention', 'archival', 'disposal', 'legal hold', 'iso15189', 'nabl'] }
        ]
      }
    ]
  },
  {
    id: 'security-admin',
    title: 'Security & Admin',
    type: 'group',
    icon: 'roles',
    items: [
      {
        id: 'roles',
        title: 'Role & Permissions (RBAC)',
        path: '/roles',
        icon: 'roles',
        screenCode: 'ROLES',
        keywords: ['role', 'permission', 'rbac', 'access', 'security', 'privilege', 'override', 'rights']
      },
      {
        id: 'employees',
        title: 'Employee Master',
        path: '/employees',
        icon: 'employees',
        screenCode: 'EMPLOYEES',
        keywords: ['employee', 'staff', 'technician', 'phlebotomist', 'user', 'hr', 'directory']
      },
      {
        id: 'audit',
        title: 'Audit Trail',
        path: '/audit',
        icon: 'audit',
        screenCode: 'AUDIT_TRAIL',
        keywords: ['audit', 'trail', 'log', 'security', 'session', 'history', 'revision', 'compliance']
      },
      {
        id: 'registration-config',
        title: 'Registration Field Config',
        path: '/registration-config',
        icon: 'sliders',
        screenCode: 'REGISTRATION_CONFIG',
        keywords: ['registration', 'field', 'mandatory', 'visibility', 'config', 'setup', 'front desk']
      },
      {
        id: 'collection-charge',
        title: 'Collection Charge Master',
        path: '/collection-charge',
        icon: 'sliders',
        screenCode: 'COLLECTION_CHARGE',
        keywords: ['collection', 'charge', 'home collection', 'phlebotomy charge', 'visit charge', 'waiver', 'override']
      },
      {
        id: 'trf-config',
        title: 'TRF Configuration',
        path: '/trf-config',
        icon: 'sliders',
        screenCode: 'REGISTRATION_CONFIG',
        keywords: ['trf', 'test requisition', 'radiology', 'print', 'requisition form', 'config']
      },
      {
        id: 'barcode-inventory',
        title: 'Pre-printed Barcode Inventory',
        path: '/barcode-inventory',
        icon: 'sliders',
        screenCode: 'REGISTRATION_CONFIG',
        keywords: ['barcode', 'pre-printed', 'preprinted', 'sticker', 'inventory', 'range', 'sample']
      },
      {
        id: 'nabl-config',
        title: 'Report & NABL Config',
        path: '/nabl-config',
        icon: 'award',
        screenCode: 'NABL_CONFIG',
        keywords: ['nabl', 'iso', '15189', 'report', 'header', 'footer', 'qr', 'accreditation', 'stamp']
      },
      {
        id: 'cancellation-policy',
        title: 'Test Cancellation Policy',
        path: '/cancellation-policy',
        icon: 'sliders',
        screenCode: 'CANCELLATION_POLICY',
        keywords: ['cancellation', 'cancel', 'policy', 'authority', 'approver', 'stage', 'config']
      }
    ]
  },
  {
    id: 'saas-platform',
    title: 'SaaS Platform Admin',
    type: 'group',
    icon: 'saas',
    items: [
      {
        id: 'saas-dashboard',
        title: 'SaaS Dashboard',
        path: '/saas/dashboard',
        icon: 'dashboard',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['saas', 'platform', 'tenant', 'overview', 'stats', 'cloud', 'multi-tenant']
      },
      {
        id: 'saas-tenants',
        title: 'Tenant Management',
        path: '/saas/tenants',
        icon: 'building',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['tenant', 'fleet', 'organization', 'client', 'suspend', 'reactivate', 'domains']
      },
      {
        id: 'saas-onboarding',
        title: 'Tenant Onboarding',
        path: '/saas/onboarding',
        icon: 'registration',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['onboarding', 'new tenant', 'provisioning', 'database', 'domain setup']
      },
      {
        id: 'saas-health',
        title: 'Platform Health',
        path: '/saas/health',
        icon: 'award',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['health', 'catalog', 'database', 'status', 'connectivity', 'latency']
      },
      {
        id: 'saas-audit',
        title: 'Security & Audit Logs',
        path: '/saas/audit',
        icon: 'audit',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['audit', 'platform audit', 'administrative actions', 'lifecycle events']
      },
      {
        id: 'saas-keys',
        title: 'Key Lifecycle',
        path: '/saas/keys',
        icon: 'config',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['keys', 'cryptography', 'secret rotation', 'envelope encryption', 'retirement']
      },
      {
        id: 'saas-clinical',
        title: 'Clinical & Reference Ranges',
        path: '/saas/clinical',
        icon: 'lab',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['investigation', 'clinical', 'reference ranges', 'ranges', 'pathologist', 'units']
      },
      {
        id: 'saas-plans',
        title: 'Plans & Subscriptions',
        path: '/saas/plans',
        icon: 'billing',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['plans', 'subscriptions', 'tiers', 'mrr', 'entitlements', 'quotas']
      },
      {
        id: 'saas-wallet',
        title: 'Credits, Wallet & Usage',
        path: '/saas/wallet',
        icon: 'coins',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['wallet', 'credits', 'topup', 'ledger', 'usage', 'invoices', 'billing']
      },
      {
        id: 'saas-centres',
        title: 'Centres & Clinical Staff',
        path: '/saas/centres',
        icon: 'building',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['centres', 'branches', 'doctors', 'staff', 'pathologist', 'approval']
      },
      {
        id: 'saas-lifecycle',
        title: 'Lifecycle & Service Controls',
        path: '/saas/lifecycle',
        icon: 'shield',
        screenCode: 'SAAS_PLATFORM',
        keywords: ['lifecycle', 'lock', 'unlock', 'suspend', 'resume', 'offboarding', 'modules']
      }
    ]
  }
];

/**
 * Filter modules and items by user permissions.
 * Guarantees zero authorization leaks without modifying underlying permission engine.
 */
export const isItemAuthorized = (item, user, can) => {
  if (!user) return false;

  const isPlatformItem = item.screenCode === 'SAAS_PLATFORM' || (item.path && item.path.startsWith('/saas'));
  const isPlatformAdmin = Boolean(
    user.isPlatformAdmin ||
    user.tenantId === 1 ||
    ['PLATFORM_ADMIN', 'SUPER_ADMIN', 'SYSTEM_ADMIN'].includes(user.roleName?.toUpperCase())
  );

  // SaaS Platform items are strictly restricted to Platform Administrators
  if (isPlatformItem) {
    return isPlatformAdmin;
  }

  // Administrator has superuser access to every module and master
  if (user.roleName?.toUpperCase() === 'ADMINISTRATOR' || user.roleId === 1) {
    return true;
  }

  // Check fine-grained screen permission via AuthContext 'can'
  if (item.screenCode && can && can(item.screenCode, 'VIEW')) {
    return true;
  }

  // Check if item's path or key is explicitly present in backend-provided user.menus
  if (user.menus && Array.isArray(user.menus)) {
    const matched = user.menus.some((m) => {
      if (m.path && item.path && m.path.toLowerCase() === item.path.toLowerCase()) return true;
      if (m.key && item.id && m.key.toLowerCase() === item.id.toLowerCase()) return true;
      if (m.key && item.screenCode && m.key.toLowerCase() === item.screenCode.toLowerCase()) return true;
      return false;
    });
    if (matched) return true;
  }

  // Doctor Digital Signatures is viewable by Pathologists and Radiologists
  if (item.id === 'm-doctor-signatures' && (user.roleName?.toUpperCase().includes('DOCTOR') || user.roleName?.toUpperCase().includes('PATHOLOGIST') || user.roleName?.toUpperCase().includes('RADIOLOGIST'))) {
    return true;
  }

  // Smart Worksheet / Technician Workbench & Template Master
  if (item.id === 'pathology-worksheet' || item.id === 'm-worksheet-templates') {
    return true;
  }

  return false;
};

/**
 * Filters the entire navigation tree based on active user authorization.
 * Empty modules/subgroups are omitted automatically.
 */
export const getAuthorizedNavigation = (user, can) => {
  if (!user) return [];

  const isAdmin = user.roleName?.toUpperCase() === 'ADMINISTRATOR' || user.roleId === 1;

  return NAVIGATION_MODULES.map((mod) => {
    if (mod.type === 'single') {
      const allowed = isItemAuthorized(mod, user, can);
      return allowed ? mod : null;
    }

    if (mod.type === 'group') {
      const filteredItems = (mod.items || []).filter((item) => isItemAuthorized(item, user, can));
      if (filteredItems.length === 0) return null;
      return {
        ...mod,
        items: filteredItems
      };
    }

    if (mod.type === 'nested_group') {
      // Masters Directory
      const canAccessMasters = isAdmin || (user.menus && user.menus.some((m) => m.path === '/masters' || m.key === 'masters'));
      if (!canAccessMasters) return null;

      const filteredSubgroups = (mod.subgroups || []).map((sub) => {
        const subItems = sub.items.filter((item) => isItemAuthorized(item, user, can));
        if (subItems.length === 0) return null;
        return {
          ...sub,
          items: subItems
        };
      }).filter(Boolean);

      if (filteredSubgroups.length === 0) return null;
      return {
        ...mod,
        subgroups: filteredSubgroups
      };
    }

    return null;
  }).filter(Boolean);
};

/**
 * Flattens all authorized leaf navigation items for search and quick jump.
 */
export const getAllAuthorizedLeafItems = (authorizedModules) => {
  const leaves = [];

  authorizedModules.forEach((mod) => {
    if (mod.type === 'single') {
      leaves.push({
        id: mod.id,
        title: mod.title,
        path: mod.path,
        icon: mod.icon,
        category: 'Dashboard',
        keywords: mod.keywords || []
      });
    } else if (mod.type === 'group') {
      (mod.items || []).forEach((item) => {
        leaves.push({
          id: item.id,
          title: item.title,
          path: item.path,
          icon: item.icon,
          badge: item.badge,
          category: mod.title,
          keywords: item.keywords || []
        });
      });
    } else if (mod.type === 'nested_group') {
      // Add hub itself
      leaves.push({
        id: 'masters-hub',
        title: 'All Masters Directory Hub',
        path: mod.hubPath,
        icon: mod.icon,
        category: 'Masters Hub',
        keywords: ['masters', 'hub', 'directory', 'catalog']
      });

      (mod.subgroups || []).forEach((sub) => {
        (sub.items || []).forEach((item) => {
          leaves.push({
            id: item.id,
            title: item.title,
            path: item.path,
            icon: item.icon || sub.icon,
            category: `Masters / ${sub.title}`,
            keywords: item.keywords || []
          });
        });
      });
    }
  });

  return leaves;
};

/**
 * Calculates hierarchical breadcrumbs based on the current location.pathname.
 */
export const getNavigationBreadcrumbs = (pathname) => {
  if (!pathname || pathname === '/' || pathname === '/dashboard') {
    return [{ label: 'Dashboard', path: '/dashboard', isCurrent: true }];
  }

  // Iterate modules
  for (const mod of NAVIGATION_MODULES) {
    if (mod.type === 'single' && mod.path === pathname) {
      return [
        { label: 'Home', path: '/dashboard' },
        { label: mod.title, path: mod.path, isCurrent: true }
      ];
    }

    if (mod.type === 'group') {
      const match = (mod.items || []).find((i) => i.path === pathname || pathname.startsWith(i.path + '/'));
      if (match) {
        return [
          { label: 'Home', path: '/dashboard' },
          { label: mod.title },
          { label: match.title, path: match.path, isCurrent: true }
        ];
      }
    }

    if (mod.type === 'nested_group') {
      if (pathname === mod.hubPath) {
        return [
          { label: 'Home', path: '/dashboard' },
          { label: mod.title, path: mod.hubPath, isCurrent: true }
        ];
      }

      for (const sub of (mod.subgroups || [])) {
        const match = sub.items.find((i) => i.path === pathname || pathname.startsWith(i.path + '/'));
        if (match) {
          return [
            { label: 'Home', path: '/dashboard' },
            { label: 'Masters', path: mod.hubPath },
            { label: sub.title },
            { label: match.title, path: match.path, isCurrent: true }
          ];
        }
      }
    }
  }

  // Fallback for custom or direct paths
  const clean = pathname.replace(/^\//, '').split('/');
  return [
    { label: 'Home', path: '/dashboard' },
    ...clean.map((c, i) => ({
      label: c.charAt(0).toUpperCase() + c.slice(1).replace(/-/g, ' '),
      isCurrent: i === clean.length - 1
    }))
  ];
};
